import { ChangeDetectorRef, Directive, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { forEach, groupBy, sortBy } from 'lodash';
import { Observable, map, switchMap, tap } from 'rxjs';

import { UserService } from '@app/services/user.service';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import { selectSelectedPortfolioId } from '@dashboards/store/selectors/property-selector.selectors';
import { GetInspectionExpireListConst } from '@main-application/inspections/models/inspection-notify-list.const';
import {
  DynamicInspectionAssigneeSelectionMode,
  InspectionRecurrencePeriod,
  InspectionScheduleType,
  InspectionType,
  RestDynamicInspectionModel,
  RestInspectionsModel,
} from '@main-application/inspections/models/rest-inspections-model.interface';
import { TimezoneService } from '@main-application/management/pages/system-configuration/components/date-time-configuration/timezone.service';
import { portfolioSelectAllPropertyIds, selectPropertiesLoading } from '@portfolio/store/portfolio.selectors';
import { InspectionTypeListConst } from '@shared/constants/inspection-type-list.const';
import { GetRecurrencePeriodTypeList } from '@shared/constants/recurrence-period-type-list.const';
import { ButtonType } from '@shared/enums/button-type';
import { EIcon } from '@shared/enums/icon.enum';
import { UserType } from '@shared/enums/user-type';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestUnitSummaryModel } from '@shared/interfaces/unit.interface';
import { RestUserModel, RestUserModelExtended } from '@shared/interfaces/user.interface';
import { DestroyService } from '@shared/services/destroy.service';
import { LocalCacheService } from '@shared/services/local-cache.service';
import { RestTemplateModel } from '@template/models/rest-template-model.interface';

import { CustomUsersIdType } from './models';

const localCacheAssociateKey = 'create-dynamic-template';
const localCachePropertyUsersId = `${localCacheAssociateKey}_property-users_bump=${new Date().toISOString()}`;
const localCachePortfolioUsersId = `${localCacheAssociateKey}_portfolio-users_bump=${new Date().toISOString()}`;

@UntilDestroy()
@Directive()
export abstract class InspectionAddBaseFormComponent extends PuSubscribable {
  protected cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected store: Store<{}> = inject(Store);
  protected userService: UserService = inject(UserService);
  protected localCacheService: LocalCacheService = inject(LocalCacheService);
  private timezoneService: TimezoneService = inject(TimezoneService);

  protected readonly ButtonType = ButtonType;
  protected readonly EIcon = EIcon;
  protected readonly InspectionScheduleType = InspectionScheduleType;
  protected readonly localCacheAssociateKey = localCacheAssociateKey;
  selectPropertiesLoading$ = this.store.select(selectPropertiesLoading);
  recurrenceOptions: IRadioButtonOption<InspectionRecurrencePeriod>[] = [];
  userProperties: IRadioButtonOption<number>[] | IRadioButtonOption<number>[][] = [];
  userListModel: RestUserModelExtended[];
  inspectionTypes: IRadioButtonOption<number>[] = [];
  today = this.timezoneService.getCurrentDateOnly();
  inspectionTemplates: RestTemplateModel[] = [];
  unitListLoading$: Observable<boolean>;
  propertyUnitsLoading$: Observable<boolean>;
  userUnits: IRadioButtonOption<number>[] = [];
  formPosted = false;
  destroy$ = inject(DestroyService);
  allPropertyIds: number[];
  expireOptions = GetInspectionExpireListConst();
  appointmentOptions = GetInspectionExpireListConst(`I don't know yet`);

  constructor() {
    super();
    this.setAllPortfolioIds();
  }

  protected calculateRecurrenceOptions(dueDate: Date) {
    this.recurrenceOptions = GetRecurrencePeriodTypeList(dueDate);
    this.cdr.detectChanges();
  }

  public useAddResidentOptionNotify(type: InspectionScheduleType) {
    const getDynamic = [
      InspectionScheduleType.DynamicNotice,
      InspectionScheduleType.DynamicMoveOut,
      InspectionScheduleType.DynamicMoveIn,
    ];

    const getRegular = [
      InspectionScheduleType.Standard,
      InspectionScheduleType.Repeating,
      InspectionScheduleType.Resident,
    ];

    const isNotifyResindet = [...getDynamic, ...getRegular].includes(type);

    if (isNotifyResindet) {
      return {
        id: CustomUsersIdType.Resident,
        isAssignToResident: true,
        firstName: '',
        lastName: '',
        userGroupType: UserType.Resident,
      } as RestUserModelExtended;
    }
    return null;
  }

  protected convertDynamicInspectionAssigneeMode(
    assigneeSelectionMode: DynamicInspectionAssigneeSelectionMode,
    manuallySelectedId: number
  ): number {
    switch (assigneeSelectionMode) {
      case DynamicInspectionAssigneeSelectionMode.DefaultAssignee:
        return CustomUsersIdType.DefaultAssignee;
      case DynamicInspectionAssigneeSelectionMode.PropertyManager:
        return CustomUsersIdType.PropertyManager;
      case DynamicInspectionAssigneeSelectionMode.Resident:
        return CustomUsersIdType.Resident;
      case DynamicInspectionAssigneeSelectionMode.ManualEntry:
        return manuallySelectedId;
    }
  }

  protected convertDynamicInspectionAssigneeModes(
    assigneeSelectionModes: DynamicInspectionAssigneeSelectionMode[],
    manuallySelectedId: number[]
  ): number[] {
    manuallySelectedId ??= [];

    forEach(assigneeSelectionModes, assigneeSelectionMode => {
      switch (assigneeSelectionMode) {
        case DynamicInspectionAssigneeSelectionMode.DefaultAssignee:
          manuallySelectedId.push(CustomUsersIdType.DefaultAssignee);
          return;
        case DynamicInspectionAssigneeSelectionMode.PropertyManager:
          manuallySelectedId.push(CustomUsersIdType.PropertyManager);

          return;
        case DynamicInspectionAssigneeSelectionMode.Resident:
          manuallySelectedId.push(CustomUsersIdType.Resident);
          return;
        case DynamicInspectionAssigneeSelectionMode.ManualEntry:
          return;
      }
    });

    return manuallySelectedId;
  }

  protected parseNotifyWhenDoneUserIds(inspection: RestDynamicInspectionModel | RestInspectionsModel): number[] {
    const getIds = inspection?.notifyWhenDoneUserIds;

    const getMapped = getIds
      ?.split(',')
      ?.map(id => parseInt(id, 10))
      ?.filter(id => !isNaN(id));

    const getResult = getMapped || [];

    if ((inspection as RestDynamicInspectionModel) != null) {
      const dInspection = inspection as RestDynamicInspectionModel;

      this.convertDynamicInspectionAssigneeModes(dInspection.addDynamicNotifyWhenDone, getResult);
    }

    if ((inspection as RestInspectionsModel)?.notifyResidentWhenDone) {
      getResult.push(CustomUsersIdType.Resident);
    }
    return [...new Set(getResult)];
  }

  protected parseNotifyWhenNonDefaultOrNASelectedUserIds(
    inspection: RestDynamicInspectionModel | RestInspectionsModel
  ): number[] {
    const getIds = inspection?.notifyWhenNonDefaultOrNASelectedUserIds;

    const getMapped = getIds
      ?.split(',')
      ?.map(id => parseInt(id, 10))
      ?.filter(id => !isNaN(id));

    const getResult = getMapped || [];

    return [...new Set(getResult)];
  }

  protected setAllPortfolioIds(): void {
    this.store
      .select(portfolioSelectAllPropertyIds)
      .pipe(
        untilDestroyed(this),
        tap(ids => (this.allPropertyIds = ids))
      )
      .subscribe()
      .untilDestroyed(this);
  }

  protected getUnitList(unitList: RestUnitSummaryModel[] = [], addAllUnits = false): IRadioButtonOption<number>[] {
    if (!unitList?.length) {
      return [];
    }

    const result: IRadioButtonOption<number>[] = [];
    if (addAllUnits) {
      result.push({
        value: -1,
        label: 'All units',
        sort: 0,
      });
    }

    return result.concat(
      sortBy(unitList, u => u.name)
        .filter(u => !u.doNotAutoCreateTurns)
        .map<IRadioButtonOption<number>>((item, index) => {
          return {
            value: item.id,
            label: item.name,
            sort: index,
          };
        })
    );
  }

  protected filterOutInspectionTypes() {
    this.inspectionTypes = [];
    const templateGroups = groupBy(this.inspectionTemplates, t => t.inspectionType);
    InspectionTypeListConst.forEach(type => {
      const templates = templateGroups[type.value];
      if (templates?.length > 0) {
        if (type.value === InspectionType.CustomInspection) {
          templates.forEach(t => {
            this.inspectionTypes.push({
              ...type,
              label: t.name,
              description: t.description,
              value: t.id,
            });
          });
        } else {
          templates.forEach(t => {
            this.inspectionTypes.push({
              ...type,
              description: t.description || type.description,
              value: templates[0].id,
            });
          });
        }
      }
    });
    this.cdr.detectChanges();
  }

  protected initUserList(propertyId: number, userTypes?: number[]): Observable<RestUserModel[]> {
    return this.getUsersByPropertyId(propertyId || -1, userTypes).pipe(
      map(users => users.filter(user => !!user.isActive))
    );
  }

  private getUsersByPropertyId(propertyId: number, userTypes?: number[]): Observable<RestUserModel[]> {
    const localPropertyKey = localCachePropertyUsersId + propertyId + `userTypes=${userTypes?.join('_') || ''}`;
    const localPortfolioKey = localCachePortfolioUsersId + propertyId + `userTypes=${userTypes?.join('_') || ''}`;

    const cachedPropertyUserList$ = this.localCacheService.get$<RestUserModel[]>(localPropertyKey, this.destroy$);
    const cachedPortfolioUserList$ = this.localCacheService.get$<RestUserModel[]>(localPortfolioKey, this.destroy$);
    return propertyId !== -1
      ? cachedPropertyUserList$ ??
          this.userService
            .getPropertyUserList(propertyId, userTypes)
            .pipe(tap(v => this.localCacheService.set(localPropertyKey, v, { associateKey: localCacheAssociateKey })))
      : cachedPortfolioUserList$ ??
          this.store
            .select(selectSelectedPortfolioId)
            .pipe(
              switchMap(portfolioId =>
                this.userService
                  .getPortfolioUserList(portfolioId)
                  .pipe(
                    tap(v => this.localCacheService.set(localPortfolioKey, v, { associateKey: localCacheAssociateKey }))
                  )
              )
            );
  }
}
